import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Button } from '@components/button';
import { IconArrowRight } from '@components/icons/arrow-right';
import { Spinner } from '@components/loading-spinner';
import { Stack } from '@components/stack';
import { TagSelectorMulti } from '@components/tag-selector';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';

import { useCreatePreboardingTeams, useLoadPreboardingTeams } from 'api/preboarding/hooks';
import { Title } from 'ui/unauthenticated/pages/common';

import {
  ContentContainer,
  PreboardingPageContainer,
  ButtonsContainer,
  PreboardingStepper,
} from './common';

import { routePaths } from '../navigation';

const TEAMS = {
  HUMAN_RESOURCES: 'Human Resources',
  IT_SUPPORT: 'IT Support',
  CUSTOMER_SERVICE: 'Customer Service',
  MARKETING: 'Marketing',
  OPERATIONS: 'Operations',
  FINANCE: 'Finance',
  LEGAL: 'Legal',
  OTHER: 'Other',
};

export const SelectTeam = () => {
  const { t } = useScopedTranslate(locale);
  const navigate = useNavigate();

  const nextPage = () => {
    navigate(routePaths.theme);
  };

  const teamsQuery = useLoadPreboardingTeams();
  const data = teamsQuery?.data;

  const createMutation = useCreatePreboardingTeams({ onSuccess: nextPage });
  const [selectedTags, setSelectedTags] = useState<string[]>([]);

  useEffect(() => {
    if (data) {
      setSelectedTags(data);
    }
  }, [data]);

  if (teamsQuery.isLoading) {
    return <Spinner />;
  }

  const handleSave = () => {
    if (!selectedTags) {
      return;
    }

    const selectedKeys = (Object.keys(TEAMS) as Array<keyof typeof TEAMS>).filter((key) =>
      selectedTags.includes(TEAMS[key])
    );
    return createMutation.mutate({ teams: selectedKeys });
  };

  return (
    <ContentContainer>
      <Stack space={2} align="center">
        <Title>{t('title')}</Title>
        <Text text={t('description')} />
        <Text font="bold16" text={t('whatTeam')} />
      </Stack>

      <Stack space={6} align="center">
        <TagSelectorMulti options={Object.values(TEAMS)} onChange={setSelectedTags} />
        <ButtonsContainer>
          <Button
            color="textSecondary"
            text={t('skip')}
            iconRight={<IconArrowRight color="textSecondary" />}
            style={{ backgroundColor: theme.color.uiTrans5 }}
            onClick={nextPage}
          />
          <Button
            variant="primary-blue"
            text={t('continue')}
            onClick={handleSave}
            disabled={!selectedTags.length}
          />
        </ButtonsContainer>
      </Stack>
      <PreboardingStepper currentIndex={1} />
    </ContentContainer>
  );
};

export const SetTeamPage = () => {
  return (
    <PreboardingPageContainer>
      <SelectTeam />
    </PreboardingPageContainer>
  );
};

const locale = {
  en: {
    title: 'Tell us a bit about yourself',
    description: 'This will help us personalise your experience',
    whatTeam: 'What type of team do you work in?',
    skip: 'Skip',
    continue: 'Continue',
  },
};
